/**
 * ErrorBoundary Component
 * Catches React errors and displays a fallback UI
 * Prevents the entire app from crashing
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    // Clear error and go to home
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0A0E27 0%, #1A1F3A 100%)',
            color: '#E6E8F0',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          {/* Error Icon */}
          <div
            style={{
              fontSize: '80px',
              marginBottom: '24px',
              animation: 'bounce 2s infinite',
            }}
          >
            ⚠️
          </div>

          {/* Error Title */}
          <h1
            style={{
              fontSize: '32px',
              fontWeight: 700,
              marginBottom: '12px',
              background: 'linear-gradient(90deg, #9D4EDD 0%, #00F5FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Oops! Something went wrong
          </h1>

          {/* Error Description */}
          <p
            style={{
              fontSize: '16px',
              color: '#9AA0B0',
              marginBottom: '32px',
              maxWidth: '500px',
            }}
          >
            Don't worry, your files are safe. The app encountered an unexpected error.
            Try refreshing the page or going back to the home page.
          </p>

          {/* Error Details (Development Only) */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details
              style={{
                marginBottom: '32px',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                maxWidth: '600px',
                textAlign: 'left',
                fontSize: '14px',
              }}
            >
              <summary style={{ cursor: 'pointer', marginBottom: '12px', fontWeight: 600 }}>
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#FF6B6B',
                  fontSize: '12px',
                  margin: 0,
                }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '12px 32px',
                background: 'linear-gradient(90deg, #9D4EDD 0%, #00F5FF 100%)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: '0 4px 12px rgba(157, 78, 221, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(157, 78, 221, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(157, 78, 221, 0.3)';
              }}
            >
              🔄 Reload Page
            </button>

            <button
              onClick={this.handleGoHome}
              style={{
                padding: '12px 32px',
                background: 'transparent',
                border: '2px solid #9D4EDD',
                borderRadius: '8px',
                color: '#E6E8F0',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(157, 78, 221, 0.1)';
                e.currentTarget.style.borderColor = '#00F5FF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#9D4EDD';
              }}
            >
              🏠 Go Home
            </button>
          </div>

          {/* Help Text */}
          <p
            style={{
              marginTop: '32px',
              fontSize: '14px',
              color: '#6E7681',
            }}
          >
            If this problem persists, please contact support or try clearing your browser cache.
          </p>

          {/* Bounce Animation */}
          <style>
            {`
              @keyframes bounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
              }
            `}
          </style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
